import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { FadeIn, FadeInLeft, FadeInRight } from '../components/motion/FadeIn';
import { SectionLabel } from '../components/shared/SectionLabel';
import { IMAGES } from '../lib/images';
import { toast } from 'sonner';
import {
  ArrowRight, CheckCircle2, TrendingUp, ShieldCheck, Ban, UserSearch,
  ClipboardList, RotateCcw, DollarSign, Sparkles, PartyPopper,
  Loader2, Lock, Bell, BadgeCheck,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = ['Owner', 'Associate', 'Office Manager', 'Treatment Coordinator', 'Other'];

const scrollToForm = () => {
  document.getElementById('waitlist-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

/* ─── HERO ─── */
const WaitlistHero = () => (
  <section className="relative overflow-hidden pt-20 sm:pt-24 pb-16 sm:pb-20">
    <div className="absolute inset-0 -z-10" style={{
      background: 'radial-gradient(1200px circle at 20% 10%, rgba(42,140,134,0.14), transparent 55%), radial-gradient(900px circle at 85% 5%, rgba(42,140,134,0.08), transparent 50%)'
    }} />
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-12 items-center">
      <FadeInLeft className="lg:col-span-6">
        <div className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--sss-navy))] text-white px-4 py-1.5 mb-5 shadow-sm">
          <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--sss-coral))]" />
          <span className="text-xs font-medium" data-testid="waitlist-hero-badge">
            New · ORCAA First Look — Full-Arch Case Recovery
          </span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[hsl(var(--sss-navy))] leading-[1.08]" data-testid="waitlist-hero-headline">
          They Said Yes to Treatment.{' '}
          <span className="text-[hsl(var(--sss-coral))]">Their Credit Said No.</span>
        </h1>
        <p className="mt-5 text-base md:text-lg text-[hsl(var(--sss-ink))]/70 max-w-prose leading-relaxed">
          What if a financing decline didn't mean losing the case? A limited group of full-arch
          practices is being invited onto the SSS Early Access Waiting List as we launch.
        </p>
        {/* Early access benefits inline */}
        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
          {['Launch updates first', 'Founding-practice benefits', 'Priority onboarding'].map((b, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-[hsl(var(--sss-navy))]/80">
              <CheckCircle2 className="h-4 w-4 text-[hsl(var(--sss-teal))]" />{b}
            </div>
          ))}
        </div>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Button
            data-testid="waitlist-hero-join-button"
            onClick={scrollToForm}
            className="bg-[hsl(var(--sss-coral))] hover:bg-[hsl(var(--sss-coral-2))] text-white rounded-xl px-8 h-12 text-base font-medium shadow-md hover:shadow-lg transition-shadow"
          >
            Join the Waiting List <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button
            data-testid="waitlist-hero-how-button"
            variant="outline"
            onClick={() => document.getElementById('waitlist-process')?.scrollIntoView({ behavior: 'smooth' })}
            className="border-[hsl(var(--sss-navy))] text-[hsl(var(--sss-navy))] hover:bg-[hsl(var(--sss-navy))] hover:text-white rounded-xl px-8 h-12 text-base font-medium"
          >
            See How It Works
          </Button>
        </div>
        <p className="mt-4 text-xs text-muted-foreground flex items-center gap-1.5">
          <Lock className="h-3 w-3" /> Free to join · No commitment · Limited founding spots
        </p>
        {/* Trust bar */}
        <div className="mt-8 rounded-2xl border border-[hsl(var(--border))] bg-white/70 backdrop-blur px-4 py-3 shadow-[var(--shadow-sm)]">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { icon: <TrendingUp className="h-4 w-4" />, title: 'Performance-Based', sub: 'Paid on funded cases only' },
              { icon: <Ban className="h-4 w-4" />, title: 'No Upfront Fee', sub: 'Nothing out of pocket' },
              { icon: <ShieldCheck className="h-4 w-4" />, title: 'No Risk to You', sub: 'Your patients, your chair' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <span className="mt-0.5 text-[hsl(var(--sss-teal))]">{item.icon}</span>
                <div>
                  <p className="text-xs sm:text-sm font-semibold text-[hsl(var(--sss-navy))]">{item.title}</p>
                  <p className="text-[11px] text-muted-foreground">{item.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </FadeInLeft>
      {/* Hero visual */}
      <FadeInRight className="lg:col-span-6">
        <div className="relative">
          <div className="rounded-2xl overflow-hidden shadow-[var(--shadow-lg)] h-[340px] sm:h-[440px]">
            <img src={IMAGES.dentistTablet} alt="Dental team reviewing a treatment plan" className="w-full h-full object-cover" loading="eager" />
          </div>
          {/* Floating: case recovered */}
          <div className="absolute -bottom-5 -left-4 sm:-left-8 bg-white rounded-xl shadow-[var(--shadow-md)] border border-[hsl(var(--border))] p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[hsl(var(--accent))] flex items-center justify-center">
              <DollarSign className="h-5 w-5 text-[hsl(var(--sss-teal))]" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[hsl(var(--sss-navy))]">Case Recovered · $30,000</p>
              <p className="text-[11px] text-muted-foreground">Success fee: $2,000 — illustrative</p>
            </div>
          </div>
          {/* Floating: finance-ready alert */}
          <div className="absolute -top-4 -right-3 sm:-right-6 bg-white rounded-xl shadow-[var(--shadow-md)] border border-[hsl(var(--border))] p-3 flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-[hsl(var(--accent))] flex items-center justify-center">
              <Bell className="h-4 w-4 text-[hsl(var(--sss-teal))]" />
            </div>
            <div>
              <p className="text-xs font-semibold text-[hsl(var(--sss-navy))]">Patient finance-ready</p>
              <p className="text-[10px] text-muted-foreground">Back to your practice</p>
            </div>
          </div>
          {/* Floating: early access pill */}
          <div className="absolute top-5 left-5">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/90 backdrop-blur px-3.5 py-1.5 shadow-sm border border-[hsl(var(--border))]">
              <BadgeCheck className="h-3.5 w-3.5 text-[hsl(var(--sss-teal))]" />
              <span className="text-[11px] font-semibold text-[hsl(var(--sss-navy))]">Early Access</span>
            </div>
          </div>
        </div>
      </FadeInRight>
    </div>
  </section>
);

/* ─── PROCESS ─── */
const steps = [
  { num: '01', icon: <UserSearch className="h-5 w-5" />, title: 'Identify', desc: "Wants treatment, can't get financing" },
  { num: '02', icon: <ClipboardList className="h-5 w-5" />, title: 'Enroll', desc: 'Your team enrolls them into SSS' },
  { num: '03', icon: <TrendingUp className="h-5 w-5" />, title: 'Improve', desc: 'Program clears the credit barriers' },
  { num: '04', icon: <RotateCcw className="h-5 w-5" />, title: 'Return', desc: 'Finance-ready, back to your practice' },
  { num: '05', icon: <DollarSign className="h-5 w-5" />, title: 'Get Funded', desc: 'Case moves ahead, production recovered' },
];

const ProcessSection = () => (
  <section id="waitlist-process" className="py-16 sm:py-20 bg-white">
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
      <FadeIn className="text-center max-w-3xl mx-auto mb-14">
        <SectionLabel>The Recovery Path</SectionLabel>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">
          From Declined to Finance-Ready to Funded
        </h2>
        <p className="mt-3 text-base text-[hsl(var(--sss-ink))]/70">
          Five steps. Your patients, your chair — and production you already earned.
        </p>
      </FadeIn>
      <div className="relative">
        {/* Connector line (desktop) */}
        <div className="hidden lg:block absolute top-[26px] left-[10%] right-[10%] border-t-2 border-dashed border-[hsl(var(--sss-teal))]/25" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {steps.map((step, i) => (
            <FadeIn key={i} delay={i * 0.06}>
              <div className="relative flex flex-col items-center text-center group" data-testid={`waitlist-step-${step.num}`}>
                <div className="relative z-10 h-14 w-14 rounded-2xl bg-[hsl(var(--accent))] flex items-center justify-center text-[hsl(var(--sss-teal))] ring-8 ring-white shadow-[var(--shadow-sm)] group-hover:-translate-y-1 transition-transform duration-200">
                  {step.icon}
                  <span className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-[hsl(var(--sss-navy))] text-white text-[10px] font-bold flex items-center justify-center">
                    {step.num}
                  </span>
                </div>
                <h3 className="mt-4 text-base font-semibold text-[hsl(var(--sss-navy))]">{step.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground leading-relaxed max-w-[200px]">{step.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ─── ECONOMICS ─── */
const EconomicsSection = () => (
  <section className="py-16 sm:py-20 bg-[hsl(var(--secondary))]">
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
      <FadeIn className="text-center max-w-3xl mx-auto mb-12">
        <SectionLabel>The Economics</SectionLabel>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">
          You Only Pay When You Get Paid
        </h2>
        <p className="mt-3 text-base text-[hsl(var(--sss-ink))]/70">
          Unheard of in this industry: performance-based pricing aligned with your production.
        </p>
      </FadeIn>
      <div className="grid gap-6 lg:grid-cols-3 items-stretch">
        {/* Column 1: production already paid for */}
        <FadeIn>
          <Card className="h-full rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-shadow">
            <CardContent className="p-6 sm:p-7">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-[hsl(var(--sss-teal))]">Already Invested</span>
              <h3 className="text-lg font-semibold text-[hsl(var(--sss-navy))] mt-1 mb-5">
                The Production You Already Paid For
              </h3>
              <div className="space-y-4">
                {[
                  { icon: <DollarSign className="h-4 w-4" />, text: 'Money spent on the lead' },
                  { icon: <UserSearch className="h-4 w-4" />, text: 'Consultation completed' },
                  { icon: <CheckCircle2 className="h-4 w-4" />, text: 'Treatment accepted' },
                  { icon: <ClipboardList className="h-4 w-4" />, text: 'Financing attempted' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-lg bg-[hsl(var(--accent))] flex items-center justify-center text-[hsl(var(--sss-teal))] shrink-0">
                      {item.icon}
                    </div>
                    <span className="text-sm text-[hsl(var(--sss-ink))]/80">{item.text}</span>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-sm text-muted-foreground leading-relaxed border-t border-dashed border-[hsl(var(--border))] pt-4">
                Then the case died at the financing application. That production doesn't have to stay lost.
              </p>
            </CardContent>
          </Card>
        </FadeIn>
        {/* Column 2: illustrative example — highlighted */}
        <FadeIn>
          <Card className="h-full rounded-2xl border-2 border-[hsl(var(--sss-teal))]/30 bg-white shadow-[var(--shadow-md)] relative">
            <div className="absolute -top-3 right-6">
              <Badge className="bg-[hsl(var(--sss-teal))] text-white text-xs px-3 py-1 hover:bg-[hsl(var(--sss-teal))]">Illustrative Example</Badge>
            </div>
            <CardContent className="p-6 sm:p-7">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-[hsl(var(--sss-teal))]">One Recovered Case</span>
              <h3 className="text-lg font-semibold text-[hsl(var(--sss-navy))] mt-1 mb-5">
                The Case That Almost Walked Out
              </h3>
              <div className="rounded-2xl bg-[hsl(var(--accent))]/60 p-5 space-y-4" data-testid="waitlist-case-example">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[hsl(var(--sss-ink))]/70">Case recovered</span>
                  <span className="text-3xl font-bold text-[hsl(var(--sss-navy))]">$30,000</span>
                </div>
                <div className="border-t border-[hsl(var(--sss-teal))]/20" />
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[hsl(var(--sss-ink))]/70">SSS success fee</span>
                  <span className="text-3xl font-bold text-[hsl(var(--sss-teal))]">$2,000</span>
                </div>
              </div>
              <p className="mt-5 text-sm text-[hsl(var(--sss-ink))]/75 leading-relaxed">
                These aren't new leads — they're <span className="font-semibold text-[hsl(var(--sss-navy))]">your patients</span>,
                and the fee is a <span className="font-semibold text-[hsl(var(--sss-teal))]">fraction</span> of the case.
              </p>
            </CardContent>
          </Card>
        </FadeIn>
        {/* Column 3: fee tiers */}
        <FadeIn>
          <Card className="h-full rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-shadow">
            <CardContent className="p-6 sm:p-7">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-[hsl(var(--sss-teal))]">Planned Success-Fee Tiers</span>
              <h3 className="text-lg font-semibold text-[hsl(var(--sss-navy))] mt-1 mb-5">
                Simple, Tiered, Transparent
              </h3>
              <div className="divide-y divide-[hsl(var(--border))]" data-testid="waitlist-fee-tiers">
                {[
                  { tier: '$10K–$20K funded', fee: '$1,000' },
                  { tier: '$20K–$30K funded', fee: '$1,500' },
                  { tier: '$30K+ funded', fee: '$2,000' },
                ].map((row, i) => (
                  <div key={i} className="flex items-center justify-between py-3.5">
                    <span className="text-sm text-[hsl(var(--sss-ink))]/80">{row.tier}</span>
                    <span className="text-lg font-bold text-[hsl(var(--sss-navy))]">{row.fee}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex items-center gap-3 rounded-xl bg-[hsl(var(--accent))]/60 px-4 py-3.5">
                <Ban className="h-4 w-4 text-[hsl(var(--sss-teal))] shrink-0" />
                <p className="text-sm font-semibold text-[hsl(var(--sss-navy))]">No funding. No success fee.</p>
              </div>
            </CardContent>
          </Card>
        </FadeIn>
      </div>
    </div>
  </section>
);

/* ─── FORM ─── */
const initialForm = {
  first_name: '', last_name: '', practice_name: '', email: '', cellphone: '',
  role: '', monthly_full_arch_volume: '', new_leads_per_month: '', message: '', website: '',
};

const WaitlistFormSection = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((er) => ({ ...er, [field]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.first_name.trim()) er.first_name = 'First name is required';
    if (!form.last_name.trim()) er.last_name = 'Last name is required';
    if (!form.practice_name.trim()) er.practice_name = 'Practice name is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = 'Enter a valid email';
    if (form.cellphone.replace(/\D/g, '').length < 7) er.cellphone = 'Enter a valid cellphone';
    if (!form.role) er.role = 'Select your role';
    if (!form.monthly_full_arch_volume.trim()) er.monthly_full_arch_volume = 'Enter your monthly full-arch case volume';
    return er;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const er = validate();
    if (Object.keys(er).length) {
      setErrors(er);
      toast.error('Please complete the highlighted fields.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Something went wrong');
      setSubmitted(true);
      toast.success("You're on the list! Watch your inbox for launch updates.");
    } catch (err) {
      toast.error(err.message || 'Unable to submit right now. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const errClass = (field) => errors[field] ? 'border-[hsl(var(--destructive))] focus-visible:ring-[hsl(var(--destructive))]' : '';

  return (
    <section id="waitlist-form" className="py-16 sm:py-20 bg-white">
      <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-5 items-stretch">
          {/* Left panel — why join */}
          <FadeInLeft className="lg:col-span-2">
            <div className="h-full rounded-2xl bg-[hsl(var(--sss-navy))] noise-overlay relative overflow-hidden p-7 sm:p-9 flex flex-col shadow-[var(--shadow-lg)]">
              <div className="absolute inset-0" style={{
                background: 'radial-gradient(600px circle at 85% 10%, rgba(42,140,134,0.28), transparent 55%)'
              }} />
              <div className="relative flex-1">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-[hsl(var(--sss-coral))]">Early Access</span>
                <h2 className="mt-2 text-2xl sm:text-3xl font-semibold text-white leading-tight">
                  Join the Waiting List
                </h2>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">
                  A limited group of full-arch practices is being invited as we launch.
                  Founding practices get:
                </p>
                <div className="mt-6 space-y-4">
                  {[
                    { title: 'Launch updates first', sub: 'Be the first to know when doors open' },
                    { title: 'Founding-practice benefits', sub: 'Reserved for the earliest partners' },
                    { title: 'Priority onboarding', sub: 'Skip the line when we go live' },
                  ].map((b, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-[hsl(var(--sss-coral))] shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-white">{b.title}</p>
                        <p className="text-xs text-white/60">{b.sub}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative mt-8 rounded-xl bg-white/10 border border-white/15 backdrop-blur px-4 py-3.5">
                <p className="text-xs text-white/80 leading-relaxed">
                  <span className="font-semibold text-white">Stop losing cases to credit.</span>{' '}
                  Performance-based and aligned with your success — you only pay when you get paid.
                </p>
              </div>
            </div>
          </FadeInLeft>

          {/* Right panel — form */}
          <FadeInRight className="lg:col-span-3">
            {submitted ? (
              <Card className="h-full rounded-2xl border border-[hsl(var(--border))] shadow-[var(--shadow-md)]" data-testid="waitlist-success-card">
                <CardContent className="h-full p-8 sm:p-12 flex flex-col items-center justify-center text-center">
                  <div className="h-16 w-16 rounded-full bg-[hsl(var(--accent))] flex items-center justify-center">
                    <PartyPopper className="h-8 w-8 text-[hsl(var(--sss-teal))]" />
                  </div>
                  <h3 className="mt-6 text-2xl font-semibold text-[hsl(var(--sss-navy))]">You're on the list!</h3>
                  <p className="mt-3 text-sm text-[hsl(var(--sss-ink))]/70 max-w-md leading-relaxed">
                    Thank you for joining the ORCAA First Look Early Access Waiting List.
                    Our team will reach out with launch updates, founding-practice benefits,
                    and your priority onboarding details.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card className="rounded-2xl border border-[hsl(var(--border))] shadow-[var(--shadow-md)]">
                <CardContent className="p-6 sm:p-8">
                  <form onSubmit={handleSubmit} noValidate data-testid="waitlist-form-element">
                    {/* Honeypot — hidden from humans */}
                    <input
                      type="text" name="website" value={form.website} onChange={set('website')}
                      tabIndex={-1} autoComplete="off" aria-hidden="true"
                      className="absolute -left-[9999px] h-0 w-0 opacity-0"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="wl-first-name" className="text-[hsl(var(--sss-navy))]">First Name *</Label>
                        <Input id="wl-first-name" data-testid="waitlist-first-name-input" value={form.first_name} onChange={set('first_name')} placeholder="Jane" className={`mt-1.5 h-11 rounded-xl ${errClass('first_name')}`} />
                        {errors.first_name && <p className="mt-1 text-xs text-[hsl(var(--destructive))]" data-testid="waitlist-first-name-error">{errors.first_name}</p>}
                      </div>
                      <div>
                        <Label htmlFor="wl-last-name" className="text-[hsl(var(--sss-navy))]">Last Name *</Label>
                        <Input id="wl-last-name" data-testid="waitlist-last-name-input" value={form.last_name} onChange={set('last_name')} placeholder="Smith" className={`mt-1.5 h-11 rounded-xl ${errClass('last_name')}`} />
                        {errors.last_name && <p className="mt-1 text-xs text-[hsl(var(--destructive))]">{errors.last_name}</p>}
                      </div>
                      <div className="sm:col-span-2">
                        <Label htmlFor="wl-practice" className="text-[hsl(var(--sss-navy))]">Practice Name *</Label>
                        <Input id="wl-practice" data-testid="waitlist-practice-input" value={form.practice_name} onChange={set('practice_name')} placeholder="Bright Smiles Dental" className={`mt-1.5 h-11 rounded-xl ${errClass('practice_name')}`} />
                        {errors.practice_name && <p className="mt-1 text-xs text-[hsl(var(--destructive))]">{errors.practice_name}</p>}
                      </div>
                      <div>
                        <Label htmlFor="wl-email" className="text-[hsl(var(--sss-navy))]">Email *</Label>
                        <Input id="wl-email" data-testid="waitlist-email-input" type="email" value={form.email} onChange={set('email')} placeholder="you@practice.com" className={`mt-1.5 h-11 rounded-xl ${errClass('email')}`} />
                        {errors.email && <p className="mt-1 text-xs text-[hsl(var(--destructive))]" data-testid="waitlist-email-error">{errors.email}</p>}
                      </div>
                      <div>
                        <Label htmlFor="wl-cell" className="text-[hsl(var(--sss-navy))]">Cellphone *</Label>
                        <Input id="wl-cell" data-testid="waitlist-cellphone-input" type="tel" value={form.cellphone} onChange={set('cellphone')} placeholder="(555) 123-4567" className={`mt-1.5 h-11 rounded-xl ${errClass('cellphone')}`} />
                        {errors.cellphone && <p className="mt-1 text-xs text-[hsl(var(--destructive))]">{errors.cellphone}</p>}
                      </div>
                      <div>
                        <Label className="text-[hsl(var(--sss-navy))]">Your Role *</Label>
                        <Select value={form.role} onValueChange={set('role')}>
                          <SelectTrigger data-testid="waitlist-role-select" className={`mt-1.5 h-11 rounded-xl ${errClass('role')}`}>
                            <SelectValue placeholder="Select role" />
                          </SelectTrigger>
                          <SelectContent>
                            {ROLES.map((r) => <SelectItem key={r} value={r} data-testid={`waitlist-role-option-${r.toLowerCase().replace(/ /g, '-')}`}>{r}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {errors.role && <p className="mt-1 text-xs text-[hsl(var(--destructive))]">{errors.role}</p>}
                      </div>
                      <div>
                        <Label htmlFor="wl-volume" className="text-[hsl(var(--sss-navy))]">Monthly Full-Arch Case Volume *</Label>
                        <Input id="wl-volume" data-testid="waitlist-volume-input" value={form.monthly_full_arch_volume} onChange={set('monthly_full_arch_volume')} placeholder="e.g., 12" className={`mt-1.5 h-11 rounded-xl ${errClass('monthly_full_arch_volume')}`} />
                        {errors.monthly_full_arch_volume && <p className="mt-1 text-xs text-[hsl(var(--destructive))]">{errors.monthly_full_arch_volume}</p>}
                      </div>
                      <div className="sm:col-span-2">
                        <Label htmlFor="wl-leads" className="text-[hsl(var(--sss-navy))]">New Leads Per Month <span className="text-muted-foreground font-normal">(optional)</span></Label>
                        <Input id="wl-leads" data-testid="waitlist-leads-input" value={form.new_leads_per_month} onChange={set('new_leads_per_month')} placeholder="e.g., 50" className="mt-1.5 h-11 rounded-xl" />
                      </div>
                      <div className="sm:col-span-2">
                        <Label htmlFor="wl-message" className="text-[hsl(var(--sss-navy))]">Anything else we should know? <span className="text-muted-foreground font-normal">(optional)</span></Label>
                        <Textarea id="wl-message" data-testid="waitlist-message-input" value={form.message} onChange={set('message')} placeholder="Tell us about your practice or ask a question…" rows={3} className="mt-1.5 rounded-xl" />
                      </div>
                    </div>
                    <Button
                      type="submit"
                      disabled={submitting}
                      data-testid="waitlist-submit-button"
                      className="mt-7 w-full bg-[hsl(var(--sss-coral))] hover:bg-[hsl(var(--sss-coral-2))] text-white rounded-xl h-12 text-base font-medium shadow-md disabled:opacity-60"
                    >
                      {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Reserving your spot…</>) : (<>Join the Waiting List <ArrowRight className="ml-2 h-4 w-4" /></>)}
                    </Button>
                    <p className="mt-4 text-center text-xs text-muted-foreground flex items-center justify-center gap-1.5">
                      <Lock className="h-3 w-3" /> Your information is never sold or shared. Free to join, no commitment.
                    </p>
                  </form>
                </CardContent>
              </Card>
            )}
          </FadeInRight>
        </div>
      </div>
    </section>
  );
};

/* ─── DISCLAIMER ─── */
const DisclaimerSection = () => (
  <section className="py-10 bg-[hsl(var(--secondary))] border-t border-[hsl(var(--border))]">
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
      <p className="text-xs text-muted-foreground leading-relaxed max-w-3xl" data-testid="waitlist-disclaimer">
        Smile Score Solutions is in development. Program structure, qualification criteria, financing
        integrations and success-fee tiers shown here are planned and subject to change. The $30,000
        case is illustrative. <span className="font-semibold">No credit result, financing approval or funding outcome is guaranteed.</span>
      </p>
      <div className="flex items-center gap-2 shrink-0">
        <Lock className="h-4 w-4 text-[hsl(var(--sss-teal))]" />
        <p className="text-xs font-medium text-[hsl(var(--sss-navy))]">Performance-Based. Aligned With <span className="text-[hsl(var(--sss-teal))]">Your Success.</span></p>
      </div>
    </div>
  </section>
);

/* ─── PAGE ─── */
export default function WaitlistPage() {
  return (
    <div data-testid="waitlist-page">
      <WaitlistHero />
      <ProcessSection />
      <EconomicsSection />
      <WaitlistFormSection />
      <DisclaimerSection />
    </div>
  );
}
